import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "text-black font-bold" }
const _hoisted_2 = {
  key: 0,
  class: "font-gotham-medium"
}
const _hoisted_3 = { class: "text-black font-bold" }
const _hoisted_4 = {
  key: 0,
  class: "text-gray-500 text-sm"
}
const _hoisted_5 = { key: 1 }

import PlanSelector from '../components/ordering/PlanSelector.vue';
import ContractLengthSelector from '../components/ordering/ContractLengthSelectorOld.vue';
import OrderForm, { ContactInformation } from '../components/ordering/OrderFormOld.vue';
import VoipSelector from '../components/ordering/VoipSelectorOld.vue';
import HardwareSelector from '../components/ordering/HardwareSelectorOld.vue';
import HeroText from '../components/content/HeroText.vue';
import Title from '../components/content/Title.vue';
import { Brand } from '../services/integration/enums';
import Container from '../components/content/Container.vue';
import { computed, onMounted, Ref, ref, reactive, watch } from 'vue';
import store from '@/store';
import { injectStrict } from '@/plugins/vueServiceContainerPlugin';
import { Catalog, CatalogAdapterInterface, Product } from '@/adapters/catalogAdapterInterface';
import router from '@/router';
import { Customer, OrderServiceInterface, ValidationError } from '@/services/orderServiceInterface';
import { ShoppingCartClientInterface } from '@/services/integration/clients/shoppingCartClientInterface';
import { Lead } from '@/services/integration/clients/types';
import { BrandServiceInterface } from '@/services/brand/brandServiceInterface';
import { PromotionsHandlerInterface } from '@/services/promotionsHandlerInterface';
import { Action, Promotion } from '@uniti/promotions-js-client/dist/utilities/types';
import PromotionDiscounts from '@/components/ordering/PromotionDiscounts.vue';
import { useGtm } from '@gtm-support/vue-gtm';

// Injections.

export default /*@__PURE__*/_defineComponent({
  __name: 'SignUpOld',
  setup(__props) {

const brandService = injectStrict<BrandServiceInterface>('BrandServiceInterface');
const catalogAdapter = injectStrict<CatalogAdapterInterface>('CatalogAdapterInterface');
const orderService = injectStrict<OrderServiceInterface>('OrderServiceInterface');
const shoppingCartService = injectStrict<ShoppingCartClientInterface>('ShoppingCartClientInterface');
const promotionsHandler = injectStrict<PromotionsHandlerInterface>('PromotionsHandlerInterface');

// Data.
const catalogs: Ref<Array<Catalog>> = ref([]);
const plans: Ref<Array<Product>> = ref([]);
const hardware: Ref<Array<Product>> = ref([]);
const voice: Ref<Array<Product>> = ref([]);
const fees: Ref<Array<Product>> = ref([]);
const serviceDetails = reactive({
  rtcDays: 0,
  relocation: 0,
  newService: 0
});

// Promotions
const suggestedPromotion: Ref<Promotion|undefined> = ref();
const validPromotion: Ref<Promotion|undefined> = ref();
const promotionDiscounts: Ref<Array<Action>> = ref([]);
const promoCode: Ref<string|undefined> = ref();

const selectedProducts: Ref<Array<Product>> = ref([]);
const selectedPlan = ref('');
const selectedContract = ref('');
const selectedVoipPlan = ref('');
const selectedHardware = ref('');
const isVoipAdded = ref(false);
const isVoipListed = ref(false);
const isHardwareAdded = ref(false);
const isPromoValid: Ref<boolean> = ref(false);
const isPlanValid: Ref<boolean> = ref(false);
const isContractValid: Ref<boolean> = ref(false);

const ready = ref(false);
const validationErrors = ref<string[]>([]);
const cartId = ref<string|undefined>('');
const crmId = ref<string|null>(null);

const gtm = useGtm();

// Computed properties.
const serviceQualification = computed(() => {
  // Get SQ from store.
  return store.state.serviceQualification;
});
const contracts = computed(() => {
  // Contracts are all fees except the new development cost.
  return fees.value.filter(fee => fee.code !== 'NDC');
});

// Watchers
watch(promoCode, async (promoCode) => {
  suggestedPromotion.value = undefined;

  if (serviceQualification.value && promoCode) {
    suggestedPromotion.value = await await promotionsHandler.suggest(serviceQualification.value, brandService.getName(), catalogs.value, promoCode);
  }

  if (promoCode && serviceQualification.value && suggestedPromotion.value === undefined) {
    validPromotion.value = await promotionsHandler.validate(serviceQualification.value, selectedProducts.value, brandService.getName(), promoCode, catalogs.value, serviceDetails);
  }

  // Remove all the existing discounts so we can re-calculate them for a new promotion.
  plans.value = promotionsHandler.removeDiscountsFromProducts(plans.value);
  fees.value = promotionsHandler.removeDiscountsFromProducts(fees.value);
  hardware.value = promotionsHandler.removeDiscountsFromProducts(hardware.value);
  promotionDiscounts.value = promotionDiscounts.value = [];

  // If suggestedPromotion exists modify products based on the promotion actions
  if (suggestedPromotion.value) {
    validPromotion.value = suggestedPromotion.value;
  }

  plans.value = promotionsHandler.mapActionsToProducts(validPromotion.value, plans.value);
  fees.value = promotionsHandler.mapActionsToProducts(validPromotion.value, fees.value);
  hardware.value = promotionsHandler.mapActionsToProducts(validPromotion.value, hardware.value);

  // Run the selected products handler to verify the promotion
  selectedProductsHandler(selectedProducts.value);
  // Set the discounts to display depending on the the promo / plan / contract
  ifPromoValid(isPromoValid.value);
  ifPlanValid(isPlanValid.value);
  ifContractValid(isContractValid.value);
});

watch(selectedProducts, async (selectedProducts) => {
  selectedProductsHandler(selectedProducts);
});

watch(isPromoValid, isPromoValid => {
  ifPromoValid(isPromoValid);
});

watch(isPlanValid, isPlanValid => {
  ifPlanValid(isPlanValid);
});

watch(isContractValid, isContractValid => {
  ifContractValid(isContractValid);
});

// Hooks.
onMounted(async () => {
  // Get catalogs for brand
  catalogs.value = await catalogAdapter.getCatalog(brandService.getName());

  if (serviceQualification.value) {
    // Return a suggested promotion from the promotions-client
    suggestedPromotion.value = await promotionsHandler.suggest(serviceQualification.value, brandService.getName(), catalogs.value, promoCode.value);

    if (suggestedPromotion.value) {
      promoCode.value = suggestedPromotion.value.code;
    }

    // Get plans for the SQ.
    plans.value = await catalogAdapter.getPlans(serviceQualification.value);
    hardware.value = await catalogAdapter.getHardware(serviceQualification.value);
    voice.value = await catalogAdapter.getVoice(serviceQualification.value);
    fees.value = await catalogAdapter.getFees(serviceQualification.value);

    // If suggestedPromotion exists modify products based on the promotion actions
    if (suggestedPromotion.value) {
      plans.value = promotionsHandler.mapActionsToProducts(suggestedPromotion.value, plans.value);
      fees.value = promotionsHandler.mapActionsToProducts(suggestedPromotion.value, fees.value);
      hardware.value = promotionsHandler.mapActionsToProducts(suggestedPromotion.value, hardware.value);
    }

    // If there's no plans we go to the
    // "Hey sorry we can't do anything" page.
    if (!plans.value.length || !fees.value.length) {
      router.push('/not-serviceable');
      return;
    }

    // Set default plan and contract.
    const plansCopy = [...plans.value];
    const sortedPlans = plansCopy.sort((a, b) => {
      return (a.price > b.price) ? -1 : 1;
    });
    const defaultPlan = sortedPlans[0];
    planSelected(defaultPlan.id);
    const defaultContract = fees.value.find(fee => fee.name === '12 Months') || fees.value[0];
    contractSelected(defaultContract.id);

    // Set the new development cost if this address requires it.
    if (serviceQualification.value.requiresNewDevelopmentCost) {
      const ndcFee = fees.value.find(fee => fee.code === 'NDC');
      if (ndcFee) {
        selectedProducts.value.push(ndcFee);
      }
    }
  } else {
    // If SQ hasn't been done, we can't use this page.
    router.push('/');
  }

  // Display user interface now.
  ready.value = true;
});

const promoCodeApplied = (promoCodeInput?: string) => {
  promoCode.value = promoCodeInput;
};

const selectedProductsHandler = async (selectedProducts: Product[]) => {
  if (serviceQualification.value && promoCode.value) {
    validPromotion.value = await promotionsHandler.validate(serviceQualification.value, selectedProducts, brandService.getName(), promoCode.value, catalogs.value, serviceDetails);
  } else {
    validPromotion.value = undefined;
  }
  // Set the validity of the promotion
  isPromoValid.value = Boolean(validPromotion.value);

  // Set if the selected contract / plan is valid - this is done so we can still display discounts on a partially validated promotion
  if (promoCode.value) {
    isContractValid.value = await promotionsHandler.isContractLengthValid(selectedProducts, promoCode.value, brandService.getName());
    isPlanValid.value = await promotionsHandler.isPlanValid(selectedProducts, promoCode.value, brandService.getName());
  } else {
    isContractValid.value = false;
    isPlanValid.value = false;
  }

  // If the promotion is valid we check to see if there is a delayedNdc or Free Year action on the promotion. This is used to render
  // promotional descriptions in the order summary.
  if (isPromoValid.value) {
    promotionDiscounts.value = [];

    const ndcProduct = selectedProducts.find(product => product.code === 'NDC');
    const internetProduct = selectedProducts.find(product => product.type === 'internet');

    if (ndcProduct) {
      const delayedNdcAction = validPromotion.value?.actions.find(action => action.actionCatalogObject.code === 'DELAYED-NDC');
      if (delayedNdcAction) promotionDiscounts.value.push(delayedNdcAction);
    }

    if (internetProduct) {
      const yearOffAction = validPromotion.value?.actions.find(action => action.actionCatalogObject.code === 'YEAR-OFF');
      if (yearOffAction) promotionDiscounts.value.push(yearOffAction);

      const oneOffAction = validPromotion.value?.actions.find(action => action.actionCatalogObject.code === 'ONE-OFF');
      if (oneOffAction) promotionDiscounts.value.push(oneOffAction);
    }

    const freePreInstalledRouterAction = validPromotion.value?.actions.find(action => action.actionCatalogObject.code === 'FREE-ROUTER-INSTALLED');
    if (freePreInstalledRouterAction) promotionDiscounts.value.push(freePreInstalledRouterAction);
  } else {
    promotionDiscounts.value = [];
  }

  if (isPromoValid.value && validPromotion.value) {
    // Remove the display discount conditions so we can re-add if applicable
    // hardware.value = promotionsHandler.removeDiscountsFromProducts(hardware.value);
    // Apply the discounts using applicable when Id's
    promotionsHandler.applyApplicableWhenConditionToProducts(validPromotion.value, selectedProducts, hardware.value);
  }
};

const ifPromoValid = (isPromoValid: boolean) => {
  // If a suggested promotion is valid we display the discounts that have been applied to the products
  if (isPromoValid) {
    hardware.value = promotionsHandler.addDisplayDiscountConditionToProducts(hardware.value);
  } else {
    hardware.value = promotionsHandler.removeDisplayDiscountConditionFromProducts(hardware.value);
  }
};

const ifPlanValid = (isPlanValid: boolean) => {
  // If a suggested promotion exists and the contract is valid we display the discounted plan fees
  if (isPlanValid) {
    fees.value = promotionsHandler.addDisplayDiscountConditionToProducts(fees.value);
  } else {
    fees.value = promotionsHandler.removeDisplayDiscountConditionFromProducts(fees.value);
  }
};

const ifContractValid = (isContractValid: boolean) => {
  // If a suggested promotion exists and the contract is valid we display the discounted plan fees
  if (isContractValid) {
    plans.value = promotionsHandler.addDisplayDiscountConditionToProducts(plans.value);
  } else {
    plans.value = promotionsHandler.removeDisplayDiscountConditionFromProducts(plans.value);
  }
};

const planSelected = (id: string) => {
  selectedPlan.value = id;

  // Remove plans.
  selectedProducts.value = selectedProducts.value.filter(product => product.type !== 'internet');

  // Add new plan.
  const plan = plans.value.find(plan => plan.id === id);
  if (plan) {
    selectedProducts.value.push(plan);
  }
};

const contractSelected = (id: string) => {
  selectedContract.value = id;

  // Remove contracts.
  selectedProducts.value = selectedProducts.value.filter(product => product.type !== 'fee' || product.code === 'NDC');

  // Add new contract.
  const contract = fees.value.find(plan => plan.id === id);
  if (contract) {
    selectedProducts.value.push(contract);
  }
};

const voipPlanSelected = (id: string) => {
  selectedVoipPlan.value = id;

  // Remove voip.
  selectedProducts.value = selectedProducts.value.filter(product => product.type !== 'voice');

  // Add new voip.
  const plan = voice.value.find(plan => plan.id === id);
  if (plan) {
    selectedProducts.value.push(plan);
  }
};

const hardwareSelected = (id: string) => {
  selectedHardware.value = id;

  // Remove hardware.
  selectedProducts.value = selectedProducts.value.filter(product => product.type !== 'hardware');

  // Add new hardware.
  const modem = hardware.value.find(plan => plan.id === id);
  if (modem) {
    selectedProducts.value.push(modem);
  }
};

const voipToggled = (event: boolean) => {
  isVoipAdded.value = event;
  if (!isVoipAdded.value) {
    // Remove hardware plans.
    selectedProducts.value = selectedProducts.value.filter(product => product.type !== 'voice');
  } else {
    // Add a default.
    voipPlanSelected(voice.value[0].id);
  }
};

const hardwareToggled = (event: boolean) => {
  isHardwareAdded.value = event;
  if (!isHardwareAdded.value) {
    // Remove voice plans.
    selectedProducts.value = selectedProducts.value.filter(product => product.type !== 'hardware');
  } else {
    // Add a default.
    hardwareSelected(hardware.value[0].id);
  }
};

const listingStatusToggled = (event: boolean) => {
  isVoipListed.value = event;
};

const confirmOrder = async (confirmOrderData: Customer, done: () => void) => {
  validationErrors.value = [];
  if (serviceQualification.value) {
    // Add the crmId to the rest of the order data
    if (crmId.value) {
      confirmOrderData.crmId = crmId.value;
    }

    try {
      // Save cart.
      cartId.value = await orderService.submitOrderToShoppingCart(serviceQualification.value, selectedProducts.value, confirmOrderData, validPromotion.value, cartId.value);

      // Save cart with payment. Two steps so
      // that if payment fails we don't create duplicate carts.
      cartId.value = await orderService.submitOrderToShoppingCartWithPayment(serviceQualification.value, selectedProducts.value, confirmOrderData, validPromotion.value, cartId.value);

      const gtmItems: [Record<string, unknown>] = [{}];
      // create product items for GA
      selectedProducts.value.map((pr) => {
        const tempItem = {
          item_name: pr.name,
          item_id: pr.id,
          price: pr.price,
          item_brand: pr.brand,
          item_category: pr.type,
          quantity: 1
        };
        gtmItems.push(tempItem);
      });

      // Save to GA
      if (gtm) {
        const dataLayer = gtm.dataLayer();
        if (dataLayer) {
          dataLayer.push({ ecommerce: null });
          dataLayer.push({
            event: 'purchase',
            ecommerce: {
              currency: 'AUD',
              value: orderService.calculateFirstInvoiceTotal(selectedProducts.value, isPromoValid.value, validPromotion.value),
              tax: 0,
              shipping: 0,
              affiliation: brandService.getName(),
              transaction_id: cartId.value,
              coupon: validPromotion.value ? validPromotion.value.code : '',
              items: gtmItems
            }
          });
        }
      }
      // Redirect.
      store.commit('setServiceQualification', null);
      router.push('/confirm');
      done();
    } catch (error) {
      if (error instanceof ValidationError) {
        validationErrors.value.push(error.message);
      }
      done();
    }
  }
};

const createUpdateLead = async (contactInformation: ContactInformation): Promise<void> => {
  if (serviceQualification.value) {
    const lead : Lead = {
      email: contactInformation.email,
      brand: brandService.getName(),
      carrier: serviceQualification.value.carrier,
      carrierId: serviceQualification.value.locationId,
      firstName: contactInformation.firstName,
      lastName: contactInformation.lastName,
      technology: serviceQualification.value.technology,
      phone: contactInformation.phoneNumber,
      address: serviceQualification.value.address
    };

    crmId.value = await shoppingCartService.updateLead(lead);
  }
};

return (_ctx: any,_cache: any) => {
  return (serviceQualification.value)
    ? (_openBlock(), _createBlock(Container, { key: 0 }, {
        default: _withCtx(() => [
          _createVNode(HeroText, {
            title: "Let's get connected.",
            subtitle: 'You\'re almost done! First, We\'ll need a few details from you to connect your new ' + _unref(brandService).getTitle() + ' internet service.'
          }, null, 8, ["subtitle"]),
          _createVNode(Title, {
            class: "mb-16 mt-0 font-gotham-medium",
            title: "Your address."
          }, {
            subtitle: _withCtx(() => [
              _createElementVNode("strong", _hoisted_1, _toDisplayString(serviceQualification.value.address), 1),
              _cache[0] || (_cache[0] = _createTextVNode(". ")),
              _cache[1] || (_cache[1] = _createElementVNode("br", null, null, -1)),
              _cache[2] || (_cache[2] = _createTextVNode("If this is not your address, you can ")),
              _cache[3] || (_cache[3] = _createElementVNode("a", {
                class: "text-blue-500 hover:text-blue-800 hover:underline transition duration-250",
                href: "/"
              }, "search for a new address", -1)),
              _cache[4] || (_cache[4] = _createTextVNode(". "))
            ]),
            _: 1
          }),
          (suggestedPromotion.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(Title, {
                  class: "mb-1",
                  title: "✨Promotions!✨"
                }, {
                  subtitle: _withCtx(() => [
                    _cache[5] || (_cache[5] = _createTextVNode(" Discounts available on the ")),
                    _createElementVNode("strong", _hoisted_3, _toDisplayString(suggestedPromotion.value.code), 1),
                    _cache[6] || (_cache[6] = _createTextVNode(" promotion "))
                  ]),
                  _: 1
                }),
                (suggestedPromotion.value.conditions)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_4, "*" + _toDisplayString(suggestedPromotion.value.conditions), 1))
                  : _createCommentVNode("", true),
                _createVNode(PromotionDiscounts, {
                  class: "mt-6 mb-16",
                  suggestedPromotion: suggestedPromotion.value
                }, null, 8, ["suggestedPromotion"])
              ]))
            : _createCommentVNode("", true),
          (ready.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(Title, {
                  class: "mb-6",
                  title: "Select a plan."
                }),
                _createVNode(PlanSelector, {
                  class: "mb-20",
                  onOnPlanSelected: planSelected,
                  selected: selectedPlan.value,
                  products: plans.value,
                  canSelectProduct: true
                }, null, 8, ["selected", "products"]),
                _createVNode(Title, {
                  class: "mb-6",
                  title: "Select a contract."
                }),
                _createVNode(ContractLengthSelector, {
                  class: "mb-20",
                  onOnContractSelected: contractSelected,
                  selected: selectedContract.value,
                  contractOptions: contracts.value
                }, null, 8, ["selected", "contractOptions"]),
                (voice.value.length)
                  ? (_openBlock(), _createBlock(Title, {
                      key: 0,
                      class: "mb-6 font-gotham-bold",
                      title: "Would you like a VOIP service?",
                      subtitle: "You can add our monthly talk service to your broadband internet plan."
                    }))
                  : _createCommentVNode("", true),
                (voice.value.length)
                  ? (_openBlock(), _createBlock(VoipSelector, {
                      key: 1,
                      class: "mb-20",
                      onOnVoipToggled: voipToggled,
                      onOnListingStatusToggled: listingStatusToggled,
                      onOnPlanSelected: voipPlanSelected,
                      isVoipListed: isVoipListed.value,
                      isVoipAdded: isVoipAdded.value,
                      selected: selectedVoipPlan.value,
                      products: voice.value
                    }, null, 8, ["isVoipListed", "isVoipAdded", "selected", "products"]))
                  : _createCommentVNode("", true),
                (hardware.value.length)
                  ? (_openBlock(), _createBlock(Title, {
                      key: 2,
                      class: "mb-6 font-gotham-bold",
                      title: "Do you need a modem?",
                      subtitle: "You can choose to purchase a router or supply your own."
                    }))
                  : _createCommentVNode("", true),
                (hardware.value.length)
                  ? (_openBlock(), _createBlock(HardwareSelector, {
                      key: 3,
                      class: "mb-20",
                      onOnHardwareToggled: hardwareToggled,
                      onOnHardwareSelected: hardwareSelected,
                      isHardwareAdded: isHardwareAdded.value,
                      selected: selectedHardware.value,
                      products: hardware.value
                    }, null, 8, ["isHardwareAdded", "selected", "products"]))
                  : _createCommentVNode("", true),
                _withDirectives(_createVNode(Title, {
                  class: "mb-6 font-gotham-bold",
                  title: "Complete your order.",
                  subtitle: "Finally, enter your customer details to finalise your internet service!"
                }, null, 512), [
                  [_vShow, _unref(brandService).getName() === _unref(Brand).HIS]
                ]),
                _createVNode(OrderForm, {
                  onOnConfirmOrder: confirmOrder,
                  onOnContactInformation: createUpdateLead,
                  onOnEnterPromoCode: promoCodeApplied,
                  errors: validationErrors.value,
                  service: serviceQualification.value,
                  products: selectedProducts.value,
                  isPromoValid: isPromoValid.value,
                  promotionDiscounts: promotionDiscounts.value,
                  suggestedPromotion: suggestedPromotion.value,
                  validPromotion: validPromotion.value,
                  class: "mb-20 mt-10"
                }, null, 8, ["errors", "service", "products", "isPromoValid", "promotionDiscounts", "suggestedPromotion", "validPromotion"])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})